import React, { useState } from 'react';
import {
  Typography,
  Box,
  Paper,
  Avatar,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  LinearProgress,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Menu,
  MenuItem
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useSelector, useDispatch } from 'react-redux';
import { openModal } from 'store/reducers/actions';
import FreeBlockModal from 'sections/premiumBlock/FreeBlockModal';
import { motion } from 'framer-motion';
import { Security } from '@mui/icons-material';
import ChevronRight from '@mui/icons-material/ChevronRight';
import {
  DownloadOutlined
  //  ReloadOutlined
} from '@ant-design/icons';
const PersonaB2CExtended = ({ selectedPersona }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const plan = useSelector((state) => state.proAccountReducer.plan);
  const trialWasActivated = useSelector((state) => state.proAccountReducer.trial_was_activated);
  const isModalOpen = useSelector((state) => state.modalReducer.isOpen);
  const isProUser = plan === 'Pro' || plan === 'Trial' || plan === 'Pro+' || plan === 'Enterprise Pro Max' || plan === 'Enterprise Pro';

  // isProUser = false;

  if (!selectedPersona) return null;

  const persona = selectedPersona || selectedPersona.description;

  // Add safety checks for B2C-specific properties
  const hasPurchaseMotivations =
    persona.purchaseMotivations &&
    persona.purchaseMotivations.emotionalTriggers &&
    Array.isArray(persona.purchaseMotivations.emotionalTriggers) &&
    persona.purchaseMotivations.emotionalTriggers.length > 0;

  const hasNeedsAndDesires =
    persona.purchaseMotivations &&
    persona.purchaseMotivations.needsAndDesires &&
    Array.isArray(persona.purchaseMotivations.needsAndDesires) &&
    persona.purchaseMotivations.needsAndDesires.length > 0;

  const hasChallengesAndPainPoints =
    persona.challengesAndPainPoints &&
    persona.challengesAndPainPoints.painPoints &&
    Array.isArray(persona.challengesAndPainPoints.painPoints) &&
    persona.challengesAndPainPoints.painPoints.length > 0;

  const hasObstacles =
    persona.challengesAndPainPoints &&
    persona.challengesAndPainPoints.obstacles &&
    Array.isArray(persona.challengesAndPainPoints.obstacles) &&
    persona.challengesAndPainPoints.obstacles.length > 0;

  const hasValuesAndBrandAffiliation =
    persona.valuesAndBrandAffiliation &&
    persona.valuesAndBrandAffiliation.coreValues &&
    Array.isArray(persona.valuesAndBrandAffiliation.coreValues) &&
    persona.valuesAndBrandAffiliation.coreValues.length > 0;

  const hasPreferredBrands =
    persona.valuesAndBrandAffiliation &&
    persona.valuesAndBrandAffiliation.preferredBrands &&
    Array.isArray(persona.valuesAndBrandAffiliation.preferredBrands) &&
    persona.valuesAndBrandAffiliation.preferredBrands.length > 0;

  const hasBrandInteractionAndLoyalty =
    persona.brandInteractionAndLoyalty &&
    persona.brandInteractionAndLoyalty.conversionTriggers &&
    Array.isArray(persona.brandInteractionAndLoyalty.conversionTriggers) &&
    persona.brandInteractionAndLoyalty.conversionTriggers.length > 0;

  const hasDigitalAndSocial =
    persona.digitalAndSocial &&
    persona.digitalAndSocial.socialMediaPlatforms &&
    Array.isArray(persona.digitalAndSocial.socialMediaPlatforms) &&
    persona.digitalAndSocial.socialMediaPlatforms.length > 0;

  const hasOnlineCommunities =
    persona.digitalAndSocial &&
    persona.digitalAndSocial.onlineCommunities &&
    Array.isArray(persona.digitalAndSocial.onlineCommunities) &&
    persona.digitalAndSocial.onlineCommunities.length > 0;

  const hasFollowedInfluencers =
    persona.digitalAndSocial &&
    persona.digitalAndSocial.followedInfluencers &&
    Array.isArray(persona.digitalAndSocial.followedInfluencers) &&
    persona.digitalAndSocial.followedInfluencers.length > 0;

  const hasPreferredCommunicationChannels =
    persona.preferredCommunicationChannels &&
    persona.preferredCommunicationChannels.channels &&
    Array.isArray(persona.preferredCommunicationChannels.channels) &&
    persona.preferredCommunicationChannels.channels.length > 0;

  const hasContentPreferences =
    persona.preferredCommunicationChannels &&
    persona.preferredCommunicationChannels.contentPreferences &&
    Array.isArray(persona.preferredCommunicationChannels.contentPreferences) &&
    persona.preferredCommunicationChannels.contentPreferences.length > 0;

  const hasBuyingTriggersAndObjections =
    persona.buyingTriggersAndObjections &&
    persona.buyingTriggersAndObjections.buyingTriggers &&
    Array.isArray(persona.buyingTriggersAndObjections.buyingTriggers) &&
    persona.buyingTriggersAndObjections.buyingTriggers.length > 0;

  const hasCommonObjections =
    persona.buyingTriggersAndObjections &&
    persona.buyingTriggersAndObjections.commonObjections &&
    Array.isArray(persona.buyingTriggersAndObjections.commonObjections) &&
    persona.buyingTriggersAndObjections.commonObjections.length > 0;

  const hasExtendedPsychographics =
    persona.extendedPsychographics &&
    persona.extendedPsychographics.personalityTraits &&
    Array.isArray(persona.extendedPsychographics.personalityTraits) &&
    persona.extendedPsychographics.personalityTraits.length > 0;

  const hasHobbiesAndInterests =
    persona.extendedPsychographics &&
    persona.extendedPsychographics.hobbiesAndInterests &&
    Array.isArray(persona.extendedPsychographics.hobbiesAndInterests) &&
    persona.extendedPsychographics.hobbiesAndInterests.length > 0;

  const hasTechnologyUsage =
    persona.behaviorsAndHabits &&
    persona.behaviorsAndHabits.technologyUsage &&
    Array.isArray(persona.behaviorsAndHabits.technologyUsage) &&
    persona.behaviorsAndHabits.technologyUsage.length > 0;

  const hasMediaConsumption =
    persona.behaviorsAndHabits &&
    persona.behaviorsAndHabits.mediaConsumption &&
    Array.isArray(persona.behaviorsAndHabits.mediaConsumption) &&
    persona.behaviorsAndHabits.mediaConsumption.length > 0;

  const renderList = (items) => {
    // Handle case where items is not an array
    if (!Array.isArray(items)) {
      // If it's a string, just display it as text
      if (typeof items === 'string') {
        return (
          <Typography variant="body1" sx={{ pl: 0 }}>
            {items}
          </Typography>
        );
      }
      // If it's not an array or string, return null
      return null;
    }

    // Handle empty array
    if (items.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      );
    }

    return (
      <List sx={{ pl: 0 }}>
        {items.map((item, index) => (
          <ListItem key={index} disablePadding>
            <ListItemText primary={`• ${item}`} primaryTypographyProps={{ variant: 'body1', component: 'span' }} />
          </ListItem>
        ))}
      </List>
    );
  };

  const renderChips = (items) => {
    // Handle case where items is not an array
    if (!Array.isArray(items)) {
      // If it's a string, just display it as text
      if (typeof items === 'string') {
        return <Typography variant="body1">{items}</Typography>;
      }
      // If it's not an array or string, return null
      return null;
    }

    // Handle empty array
    if (items.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          No data available
        </Typography>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {items.map((item, index) => (
          <Chip key={index} label={item} variant="outlined" size="small" />
        ))}
      </Box>
    );
  };

  const getScoreColor = (score) => {
    if (score >= 9) return `${theme.palette.success.main}99`;
    if (score >= 8) return `${theme.palette.success.light}99`;
    if (score >= 7) return `${theme.palette.warning.main}99`;
    return `${theme.palette.error.main}99`;
  };

  const renderScoreBar = (item, key) => (
    <Box key={key || `score-bar-${item.description}`} sx={{ width: '100%', mb: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="body1" sx={{ flex: 1 }}>
          {item.description}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: getScoreColor(item.final_score),
            ml: 2,
            fontWeight: 500
          }}
        >
          {item.final_score}/10
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={item.final_score * 10}
        sx={{
          height: 6,
          borderRadius: 1,
          bgcolor: theme.palette.secondary.light,
          '& .MuiLinearProgress-bar': {
            bgcolor: getScoreColor(item.final_score)
          }
        }}
      />
    </Box>
  );

  const renderCoreValues = (values) => {
    // Add safety check to ensure values is an array
    const valuesArray = Array.isArray(values) ? values : [];
    return (
      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
        {valuesArray.map((value, idx, arr) => {
          // Split on the first dash, en dash, or colon (dash escaped)
          const match = value.match(/^(.+?)[–\-:](.+)$/);
          let title, description;
          if (match) {
            title = match[1].trim();
            description = match[2].trim();
          } else {
            title = value.trim();
            description = '';
          }
          return (
            <React.Fragment key={title + idx}>
              <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mr: 1, mb: '-3px' }} />
              <strong>{title}</strong>: {description}
              {idx !== arr.length - 1 && <br />}
            </React.Fragment>
          );
        })}
      </Typography>
    );
  };

  const handleTrialClick = () => {
    dispatch(
      openModal({
        title: 'Get access to detailed persona analysis',
        subtitle: "Upgrade to Pro to view all persona details. Don't miss out on accessing more insights!",
        trial: !trialWasActivated
      })
    );
  };

  const handleDownloadClick = (event) => {
    if (!isProUser) {
      dispatch(
        openModal({
          title: 'Need to download persona details?',
          subtitle: "Upgrade to Pro to download detailed persona analysis. Don't miss out on accessing your insights!",
          trial: !trialWasActivated
        })
      );
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const convertToRTF = (persona) => {
    let rtf = '{\\rtf1\\ansi\\deff0{\\fonttbl{\\f0 Arial;}}\n';

    const escapeRTF = (text) => {
      if (typeof text !== 'string') {
        text = JSON.stringify(text) || '';
      }
      return text.replace(/\\/g, '\\\\').replace(/{/g, '\\{').replace(/}/g, '\\}');
    };

    // Basic Info
    rtf += `\\b\\fs32 ${escapeRTF(persona.name || 'Unnamed Persona')}\\b0\\fs20\\par\n`;
    rtf += `${escapeRTF(persona.age || 'N/A')} - ${escapeRTF(persona.gender || 'N/A')} - ${escapeRTF(persona.location || 'N/A')}\\par\n`;
    rtf += `${escapeRTF(persona.education || 'N/A')} - ${escapeRTF(persona.familyStatus || 'N/A')}\\par\\par\n`;

    // Lifestyle Summary
    rtf += `\\b\\fs28 Lifestyle Summary\\b0\\fs20\\par\n`;
    rtf += `${escapeRTF(persona.lifestyleSummary || 'No lifestyle summary available')}\\par\\par\n`;

    // Personal Profile
    rtf += `\\b\\fs28 Personal Profile\\b0\\fs20\\par\n`;
    rtf += `\\b Personality Traits:\\b0\\par\n`;
    if (hasExtendedPsychographics) {
      persona.extendedPsychographics.personalityTraits.forEach((trait) => {
        rtf += `- ${escapeRTF(trait)}\\par\n`;
      });
    }
    rtf += `\\par\\b Lifestyle:\\b0 ${escapeRTF(persona.extendedPsychographics?.lifestyleRoutine || 'Not available')}\\par\n`;
    rtf += `\\b Hobbies & Interests:\\b0\\par\n`;
    if (hasHobbiesAndInterests) {
      persona.extendedPsychographics.hobbiesAndInterests.forEach((hobby) => {
        rtf += `- ${escapeRTF(hobby)}\\par\n`;
      });
    }
    rtf += `\\par\n`;

    // Purchase Motivations
    rtf += `\\b\\fs28 Purchase Motivations\\b0\\fs20\\par\n`;
    rtf += `\\b Emotional Triggers:\\b0\\par\n`;
    if (hasPurchaseMotivations) {
      persona.purchaseMotivations.emotionalTriggers.forEach((trigger) => {
        rtf += `- ${escapeRTF(trigger.description)}: ${trigger.final_score}/10\\par\n`;
      });
    }
    rtf += `\\par\\b Needs & Desires:\\b0\\par\n`;
    if (hasNeedsAndDesires) {
      persona.purchaseMotivations.needsAndDesires.forEach((need) => {
        rtf += `- ${escapeRTF(need.description)}: ${need.final_score}/10\\par\n`;
      });
    }
    rtf += `\\par\n`;

    // Challenges & Pain Points
    rtf += `\\b\\fs28 Challenges & Pain Points\\b0\\fs20\\par\n`;
    rtf += `\\b Pain Points:\\b0\\par\n`;
    if (hasChallengesAndPainPoints) {
      persona.challengesAndPainPoints.painPoints.forEach((point) => {
        rtf += `- ${escapeRTF(point.description)}: ${point.final_score}/10\\par\n`;
      });
    }
    rtf += `\\par\\b Obstacles:\\b0\\par\n`;
    if (hasObstacles) {
      persona.challengesAndPainPoints.obstacles.forEach((obstacle) => {
        rtf += `- ${escapeRTF(obstacle.description)}: ${obstacle.final_score}/10\\par\n`;
      });
    }
    rtf += `\\par\n`;

    // Values & Brand Affiliation
    rtf += `\\b\\fs28 Values & Brand Affiliation\\b0\\fs20\\par\n`;
    rtf += `\\b Core Values:\\b0\\par\n`;
    if (hasValuesAndBrandAffiliation) {
      persona.valuesAndBrandAffiliation.coreValues.forEach((value) => {
        rtf += `- ${escapeRTF(value)}\\par\n`;
      });
    }
    rtf += `\\par\\b Brand Loyalty:\\b0\\par\n${escapeRTF(persona.valuesAndBrandAffiliation?.brandLoyalty || 'Not available')}\\par\n`;
    rtf += `\\b Preferred Brands:\\b0\\par\n`;
    if (hasPreferredBrands) {
      persona.valuesAndBrandAffiliation.preferredBrands.forEach((brand) => {
        rtf += `- ${escapeRTF(brand)}\\par\n`;
      });
    }
    rtf += `\\par\n`;

    // Brand Interaction
    rtf += `\\b\\fs28 Brand Interaction\\b0\\fs20\\par\n`;
    rtf += `\\b Discovery Channel:\\b0\\par\n${escapeRTF(persona.brandInteractionAndLoyalty?.discoveryChannel || 'Not available')}\\par\n`;
    rtf += `\\b Conversion Triggers:\\b0\\par\n`;
    if (hasBrandInteractionAndLoyalty) {
      persona.brandInteractionAndLoyalty.conversionTriggers.forEach((trigger) => {
        rtf += `- ${escapeRTF(trigger)}\\par\n`;
      });
    }
    rtf += `\\b Post Purchase Behavior:\\b0\\par\n${escapeRTF(
      persona.brandInteractionAndLoyalty?.postPurchaseBehavior || 'Not available'
    )}\\par\n`;
    rtf += `\\b Customer Journey:\\b0\\par\n`;
    if (persona.brandInteractionAndLoyalty?.customerJourneyStages) {
      Object.entries(persona.brandInteractionAndLoyalty.customerJourneyStages).forEach(([stage, description]) => {
        rtf += `- ${escapeRTF(stage)}: ${escapeRTF(description)}\\par\n`;
      });
    }
    rtf += `\\par\n`;

    // Financial Profile
    rtf += `\\b\\fs28 Financial Profile\\b0\\fs20\\par\n`;
    rtf += `\\b Household Income:\\b0 ${escapeRTF(persona.financialProfile?.householdIncome || 'Not available')}\\par\n`;
    rtf += `\\b Disposable Income:\\b0 ${escapeRTF(persona.financialProfile?.disposableIncome || 'Not available')}\\par\n`;
    rtf += `\\b Price Sensitivity:\\b0 ${escapeRTF(persona.financialProfile?.priceSensitivity || 'Not available')}\\par\\par\n`;

    // Digital Presence
    rtf += `\\b\\fs28 Digital Presence\\b0\\fs20\\par\n`;
    rtf += `\\b Social Media Platforms:\\b0\\par\n`;
    if (hasDigitalAndSocial) {
      persona.digitalAndSocial.socialMediaPlatforms.forEach((platform) => {
        rtf += `- ${escapeRTF(platform)}\\par\n`;
      });
    }
    rtf += `\\b Online Communities:\\b0\\par\n`;
    if (hasOnlineCommunities) {
      persona.digitalAndSocial.onlineCommunities.forEach((community) => {
        rtf += `- ${escapeRTF(community)}\\par\n`;
      });
    }
    rtf += `\\b Followed Influencers:\\b0\\par\n`;
    if (hasFollowedInfluencers) {
      persona.digitalAndSocial.followedInfluencers.forEach((influencer) => {
        rtf += `- ${escapeRTF(influencer)}\\par\n`;
      });
    }
    rtf += `\\par\n`;

    // Communication Preferences
    rtf += `\\b\\fs28 Communication Preferences\\b0\\fs20\\par\n`;
    rtf += `\\b Channels:\\b0\\par\n`;
    if (hasPreferredCommunicationChannels) {
      persona.preferredCommunicationChannels.channels.forEach((channel) => {
        rtf += `- ${escapeRTF(channel)}\\par\n`;
      });
    }
    rtf += `\\b Content Preferences:\\b0\\par\n`;
    if (hasContentPreferences) {
      persona.preferredCommunicationChannels.contentPreferences.forEach((preference) => {
        rtf += `- ${escapeRTF(preference)}\\par\n`;
      });
    }
    rtf += `\\par\n`;

    // Buying Behavior
    rtf += `\\b\\fs28 Buying Behavior\\b0\\fs20\\par\n`;
    rtf += `\\b Shopping Behavior:\\b0\\par\n${escapeRTF(persona.behaviorsAndHabits?.shoppingBehavior || 'Not available')}\\par\n`;
    rtf += `\\b Buying Triggers:\\b0\\par\n`;
    if (hasBuyingTriggersAndObjections) {
      persona.buyingTriggersAndObjections.buyingTriggers.forEach((trigger) => {
        rtf += `- ${escapeRTF(trigger)}\\par\n`;
      });
    }
    rtf += `\\b Common Objections:\\b0\\par\n`;
    if (hasCommonObjections) {
      persona.buyingTriggersAndObjections.commonObjections.forEach((objection) => {
        rtf += `- ${escapeRTF(objection)}\\par\n`;
      });
    }

    rtf += '}';
    return rtf;
  };

  const downloadDOC = () => {
    const rtfContent = convertToRTF(persona);
    const blob = new Blob([rtfContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${persona.name || 'persona'}_persona.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    handleClose();
  };

  return (
    <Paper elevation={3} sx={{ p: 4, borderRadius: 8 }}>
      {!isProUser && isModalOpen && <FreeBlockModal />}

      {/* Header with download button */}
      <Grid container spacing={3} alignItems="center" sx={{ mb: 4 }}>
        <Grid item>
          <Avatar src={persona.image_url || undefined} sx={{ width: 100, height: 100 }} />
        </Grid>
        <Grid item xs>
          <Typography variant="h4" gutterBottom>
            {persona.name || 'Unnamed Persona'}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {persona.age || 'N/A'} • {persona.gender || 'N/A'} • {persona.location || 'N/A'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {persona.education || 'N/A'} • {persona.familyStatus || 'N/A'}
          </Typography>
        </Grid>
        <Grid item>
          <Tooltip title={!isProUser ? 'Upgrade to Pro to export' : 'Download'}>
            <IconButton onClick={handleDownloadClick}>
              <DownloadOutlined />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            PaperProps={{
              sx: {
                border: 1,
                borderColor: 'secondary.light',
                borderRadius: 1
              }
            }}
          >
            <MenuItem onClick={downloadDOC}>Download as .doc</MenuItem>
          </Menu>
        </Grid>
      </Grid>

      <Divider sx={{ mb: 4 }} />

      {/* Main Content */}
      <Grid container spacing={4}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          {/* Lifestyle Summary */}
          <Typography variant="h5" color="primary" gutterBottom>
            Lifestyle Summary
          </Typography>
          <Typography variant="body1" paragraph>
            {persona.lifestyleSummary || 'No lifestyle summary available'}
          </Typography>

          {/* Extended Psychographics Section */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" color="primary" gutterBottom>
              Personal Profile
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Personality Traits:
              </Typography>
              {hasExtendedPsychographics && renderChips(persona.extendedPsychographics.personalityTraits)}
            </Box>
            <Typography variant="body1" paragraph>
              <strong>Lifestyle:</strong> {persona.extendedPsychographics?.lifestyleRoutine || 'Not available'}
            </Typography>
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Hobbies & Interests:
              </Typography>
              {hasHobbiesAndInterests && renderChips(persona.extendedPsychographics.hobbiesAndInterests)}
            </Box>
          </Box>

          {/* Purchase Motivations and Challenges */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" color="primary" gutterBottom>
              Purchase Motivations
            </Typography>

            {/* Emotional Triggers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              whileHover={{ scale: 1.01 }}
            >
              <Box
                sx={{
                  mb: 4,
                  p: 3,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  position: 'relative',
                  isolation: 'isolate',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    padding: '1px',
                    borderRadius: 'inherit',
                    background: 'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, ' + 'linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                    zIndex: -1
                  },
                  boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                  }
                }}
              >
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Emotional Triggers:
                </Typography>
                {/* Show first item normally */}
                {hasPurchaseMotivations && renderScoreBar(persona.purchaseMotivations.emotionalTriggers[0], 'emotional-trigger-first')}

                {/* Blur remaining items with premium overlay */}
                {!isProUser && hasPurchaseMotivations && persona.purchaseMotivations.emotionalTriggers.length > 1 && (
                  <Box sx={{ position: 'relative', mt: 3 }}>
                    <Box
                      sx={{
                        filter: 'blur(5px)',
                        userSelect: 'none',
                        opacity: 0.6
                      }}
                    >
                      {persona.purchaseMotivations.emotionalTriggers
                        .slice(1)
                        .map((trigger, index) => renderScoreBar(trigger, `emotional-trigger-${index}`))}
                    </Box>
                    <Box
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Stack spacing={2} alignItems="center">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            justifyContent: 'center'
                          }}
                        >
                          <Security sx={{ fontSize: 28, color: 'primary.main' }} />
                          <Typography variant="h5" sx={{ fontWeight: 600, mb: 0 }}>
                            Additional Triggers Details
                          </Typography>
                        </Box>
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 0 }}>
                          Start free trial to access all emotional triggers and understand your customer&apos;s motivations better.
                        </Typography>
                        <Box>
                          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                            <Button variant="gradient" color="primary" onClick={handleTrialClick}>
                              Start Free Trial
                            </Button>
                          </motion.div>
                        </Box>
                      </Stack>
                    </Box>
                  </Box>
                )}
                {/* Show all items for pro users */}
                {isProUser &&
                  hasPurchaseMotivations &&
                  persona.purchaseMotivations.emotionalTriggers.length > 1 &&
                  persona.purchaseMotivations.emotionalTriggers
                    .slice(1)
                    .map((trigger, index) => renderScoreBar(trigger, `emotional-trigger-pro-${index}`))}
              </Box>
            </motion.div>

            {/* Needs & Desires */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              whileHover={{ scale: 1.01 }}
            >
              <Box
                sx={{
                  mb: 4,
                  p: 3,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  position: 'relative',
                  isolation: 'isolate',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    padding: '1px',
                    borderRadius: 'inherit',
                    background: 'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, ' + 'linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                    zIndex: -1
                  },
                  boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                  }
                }}
              >
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Needs & Desires:
                </Typography>
                {!isProUser ? (
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      sx={{
                        filter: 'blur(5px)',
                        userSelect: 'none',
                        opacity: 0.6
                      }}
                    >
                      {hasNeedsAndDesires &&
                        persona.purchaseMotivations.needsAndDesires.map((need, index) => renderScoreBar(need, `need-desire-${index}`))}
                    </Box>
                    <Box
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Stack spacing={2} alignItems="center">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            justifyContent: 'center'
                          }}
                        >
                          <Security sx={{ fontSize: 28, color: 'primary.main' }} />
                          <Typography variant="h5" sx={{ fontWeight: 600, mb: 0 }}>
                            Needs & Desires Details
                          </Typography>
                        </Box>
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 0 }}>
                          Start free trial to access detailed persona insights and understand your customer&apos;s needs better.
                        </Typography>
                        <Box>
                          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                            <Button variant="gradient" color="primary" onClick={handleTrialClick}>
                              Start Free Trial
                            </Button>
                          </motion.div>
                        </Box>
                      </Stack>
                    </Box>
                  </Box>
                ) : (
                  hasNeedsAndDesires &&
                  persona.purchaseMotivations.needsAndDesires.map((need, index) => renderScoreBar(need, `need-desire-pro-${index}`))
                )}
              </Box>
            </motion.div>

            {/* Challenges & Pain Points */}
            <Typography variant="h5" color="primary" gutterBottom>
              Challenges & Pain Points
            </Typography>

            {/* Pain Points */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              whileHover={{ scale: 1.01 }}
            >
              <Box
                sx={{
                  mb: 4,
                  p: 3,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  position: 'relative',
                  isolation: 'isolate',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    padding: '1px',
                    borderRadius: 'inherit',
                    background: 'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, ' + 'linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                    zIndex: -1
                  },
                  boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                  }
                }}
              >
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Pain Points:
                </Typography>
                {!isProUser ? (
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      sx={{
                        filter: 'blur(5px)',
                        userSelect: 'none',
                        opacity: 0.6
                      }}
                    >
                      {hasChallengesAndPainPoints &&
                        persona.challengesAndPainPoints.painPoints.map((point, index) => renderScoreBar(point, `pain-point-${index}`))}
                    </Box>
                    <Box
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Stack spacing={2} alignItems="center">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            justifyContent: 'center'
                          }}
                        >
                          <Security sx={{ fontSize: 28, color: 'primary.main' }} />
                          <Typography variant="h5" sx={{ fontWeight: 600, mb: 0 }}>
                            Pain Points Details
                          </Typography>
                        </Box>
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 0 }}>
                          Start free trial to access detailed persona insights and understand your customer&apos;s pain points better.
                        </Typography>
                        <Box>
                          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                            <Button variant="gradient" color="primary" onClick={handleTrialClick}>
                              Start Free Trial
                            </Button>
                          </motion.div>
                        </Box>
                      </Stack>
                    </Box>
                  </Box>
                ) : (
                  persona.challengesAndPainPoints.painPoints.map((point, index) => renderScoreBar(point, `pain-point-pro-${index}`))
                )}
              </Box>
            </motion.div>

            {/* Obstacles */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              whileHover={{ scale: 1.01 }}
            >
              <Box
                sx={{
                  mb: 4,
                  p: 3,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  position: 'relative',
                  isolation: 'isolate',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    padding: '1px',
                    borderRadius: 'inherit',
                    background: 'linear-gradient(135deg, rgba(0,118,255,0.5) 0%, rgba(103,58,183,0.5) 60%, rgba(103,58,183,0.2) 100%)',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, ' + 'linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                    zIndex: -1
                  },
                  boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(0,0,0,0.03)'
                  }
                }}
              >
                <Typography variant="h5" sx={{ mb: 2 }}>
                  Obstacles:
                </Typography>
                {!isProUser ? (
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      sx={{
                        filter: 'blur(5px)',
                        userSelect: 'none',
                        opacity: 0.6
                      }}
                    >
                      {hasObstacles &&
                        persona.challengesAndPainPoints.obstacles.map((obstacle, index) => renderScoreBar(obstacle, `obstacle-${index}`))}
                    </Box>
                    <Box
                      sx={{
                        p: 3,
                        textAlign: 'center',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Stack spacing={2} alignItems="center">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            justifyContent: 'center'
                          }}
                        >
                          <Security sx={{ fontSize: 28, color: 'primary.main' }} />
                          <Typography variant="h5" sx={{ fontWeight: 600, mb: 0 }}>
                            Obstacles Details
                          </Typography>
                        </Box>
                        <Typography variant="body1" color="text.secondary" sx={{ mt: 0 }}>
                          Start free trial to access detailed persona insights and understand your customer&apos;s obstacles better.
                        </Typography>
                        <Box>
                          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                            <Button variant="gradient" color="primary" onClick={handleTrialClick}>
                              Start Free Trial
                            </Button>
                          </motion.div>
                        </Box>
                      </Stack>
                    </Box>
                  </Box>
                ) : (
                  hasObstacles &&
                  persona.challengesAndPainPoints.obstacles.map((obstacle, index) => renderScoreBar(obstacle, `obstacle-pro-${index}`))
                )}
              </Box>
            </motion.div>
          </Box>

          {/* Values section */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" color="primary" gutterBottom>
              Values
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 0 }}>
                Core Values:
              </Typography>
              {isProUser ? (
                hasValuesAndBrandAffiliation && renderCoreValues(persona.valuesAndBrandAffiliation.coreValues)
              ) : (
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      filter: 'blur(5px)',
                      userSelect: 'none',
                      opacity: 0.6
                    }}
                  >
                    {hasValuesAndBrandAffiliation && renderCoreValues(persona.valuesAndBrandAffiliation.coreValues)}
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      opacity: 0.5,
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                    onClick={handleTrialClick}
                  >
                    <VisibilityIcon fontSize="small" />
                    <Typography variant="caption" color="black" ml={1}>
                      View
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <strong>Brand Loyalty:</strong>{' '}
              {isProUser ? (
                persona.valuesAndBrandAffiliation?.brandLoyalty || 'Not available'
              ) : (
                <Box component="span" sx={{ position: 'relative', display: 'inline-block' }}>
                  <Box
                    component="span"
                    sx={{
                      filter: 'blur(5px)',
                      userSelect: 'none',
                      opacity: 0.6,
                      display: 'inline-block'
                    }}
                  >
                    {persona.valuesAndBrandAffiliation?.brandLoyalty || 'Not available'}
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      opacity: 0.5,
                      cursor: 'pointer',
                      padding: '4px 8px',
                      borderRadius: 1,
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                    onClick={handleTrialClick}
                  >
                    <VisibilityIcon fontSize="small" />
                    <Typography variant="caption" color="black" ml={1}>
                      View
                    </Typography>
                  </Box>
                </Box>
              )}
            </Typography>
          </Box>

          {/* Brand Interaction & Loyalty */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" color="primary" gutterBottom>
              Brand Interaction
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Discovery Channel:
              </Typography>
              {isProUser ? (
                <Typography variant="body1">{persona.brandInteractionAndLoyalty?.discoveryChannel || 'Not available'}</Typography>
              ) : (
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      filter: 'blur(5px)',
                      userSelect: 'none',
                      opacity: 0.6
                    }}
                  >
                    <Typography variant="body1">{persona.brandInteractionAndLoyalty.discoveryChannel}</Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      opacity: 0.5,
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                    onClick={handleTrialClick}
                  >
                    <VisibilityIcon fontSize="small" />
                    <Typography variant="caption" color="black" ml={1}>
                      View
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Conversion Triggers:
              </Typography>
              {isProUser ? (
                hasBrandInteractionAndLoyalty && renderChips(persona.brandInteractionAndLoyalty.conversionTriggers)
              ) : (
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      filter: 'blur(5px)',
                      userSelect: 'none',
                      opacity: 0.6
                    }}
                  >
                    {hasBrandInteractionAndLoyalty && renderChips(persona.brandInteractionAndLoyalty.conversionTriggers)}
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      opacity: 0.5,
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                    onClick={handleTrialClick}
                  >
                    <VisibilityIcon fontSize="small" />
                    <Typography variant="caption" color="black" ml={1}>
                      View
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Post Purchase Behavior:
              </Typography>
              {isProUser ? (
                <Typography variant="body1">{persona.brandInteractionAndLoyalty?.postPurchaseBehavior || 'Not available'}</Typography>
              ) : (
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      filter: 'blur(5px)',
                      userSelect: 'none',
                      opacity: 0.6
                    }}
                  >
                    <Typography variant="body1">{persona.brandInteractionAndLoyalty?.postPurchaseBehavior || 'Not available'}</Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      opacity: 0.5,
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                    onClick={handleTrialClick}
                  >
                    <VisibilityIcon fontSize="small" />
                    <Typography variant="caption" color="black" ml={1}>
                      View
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
            <Box>
              <Typography variant="subtitle1">Customer Journey:</Typography>
              {isProUser ? (
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mt: 1 }}>
                  {persona.brandInteractionAndLoyalty?.customerJourneyStages &&
                    Object.entries(persona.brandInteractionAndLoyalty.customerJourneyStages).map(([stage, description], idx, arr) => (
                      <React.Fragment key={stage}>
                        <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mr: 1, mb: '-3px' }} />
                        <span style={{ fontWeight: 'bold' }}>
                          <strong>{stage}:</strong>
                        </span>{' '}
                        {description}
                        {idx !== arr.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                </Typography>
              ) : (
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line', mt: 1 }}>
                  {persona.brandInteractionAndLoyalty?.customerJourneyStages &&
                    Object.entries(persona.brandInteractionAndLoyalty.customerJourneyStages).map(([stage, description], idx, arr) => (
                      <React.Fragment key={stage}>
                        <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mr: 1, mb: '-3px' }} />
                        <span style={{ fontWeight: 'bold' }}>
                          <strong>{stage}:</strong>
                        </span>{' '}
                        <Box component="span" sx={{ position: 'relative', display: 'inline-block' }}>
                          <Box
                            component="span"
                            sx={{
                              filter: 'blur(5px)',
                              userSelect: 'none',
                              opacity: 0.6
                            }}
                          >
                            {description}
                          </Box>
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: 'rgba(255, 255, 255, 0.1)',
                              opacity: 0.5,
                              cursor: 'pointer',
                              padding: '2px 8px',
                              borderRadius: 1,
                              whiteSpace: 'nowrap',
                              '&:hover': {
                                opacity: 1
                              }
                            }}
                            onClick={handleTrialClick}
                          >
                            <VisibilityIcon fontSize="small" />
                            <Typography variant="caption" color="black" ml={1}>
                              View
                            </Typography>
                          </Box>
                        </Box>
                        {idx !== arr.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              pl: 3,
              borderLeft: '2px solid',
              borderLeftColor: 'primary.main',
              height: '100%'
            }}
          >
            {/* Financial Profile */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" color="primary" gutterBottom>
                Financial Profile
              </Typography>
              {(() => {
                const financial = persona.financialProfile || {};
                const items = [
                  { key: 'Household Income', value: financial.householdIncome },
                  { key: 'Disposable Income', value: financial.disposableIncome },
                  { key: 'Price Sensitivity', value: financial.priceSensitivity }
                ];
                return (
                  <Box>
                    {items.map((item, index) => (
                      <Box key={index} sx={{ mb: 1 }}>
                        {isProUser ? (
                          <Typography>
                            <strong>{item.key}:</strong> {item.value || '—'}
                          </Typography>
                        ) : (
                          <>
                            <Typography sx={{ display: 'block' }}>
                              <strong>{item.key}:</strong>
                            </Typography>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, pl: 2, mt: 0.5 }}>
                              <Box
                                component="span"
                                sx={{
                                  filter: 'blur(5px)',
                                  userSelect: 'none',
                                  opacity: 0.6
                                }}
                              >
                                ********
                              </Box>
                              <Box
                                sx={{
                                  position: 'relative',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  cursor: 'pointer',
                                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                  opacity: 0.5,
                                  padding: '2px 8px',
                                  borderRadius: 1,
                                  whiteSpace: 'nowrap',
                                  transition: 'opacity 0.2s',
                                  '&:hover': {
                                    opacity: 1
                                  }
                                }}
                                onClick={handleTrialClick}
                              >
                                <VisibilityIcon fontSize="small" />
                                <Typography variant="caption" color="black" ml={1}>
                                  View
                                </Typography>
                              </Box>
                            </Box>
                          </>
                        )}
                      </Box>
                    ))}
                  </Box>
                );
              })()}
            </Box>

            {/* Brand Preferences */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" color="primary" gutterBottom>
                Brand Preferences
              </Typography>
              {isProUser ? (
                hasPreferredBrands && renderChips(persona.valuesAndBrandAffiliation.preferredBrands)
              ) : (
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      filter: 'blur(5px)',
                      userSelect: 'none',
                      opacity: 0.6
                    }}
                  >
                    {hasPreferredBrands && renderChips(persona.valuesAndBrandAffiliation.preferredBrands)}
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      opacity: 0.5,
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                    onClick={handleTrialClick}
                  >
                    <VisibilityIcon fontSize="small" />
                    <Typography variant="caption" color="black" ml={1}>
                      View
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Shopping Behavior */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" color="primary" gutterBottom>
                Shopping Behavior
              </Typography>
              {isProUser ? (
                <Typography variant="body1">{persona.behaviorsAndHabits?.shoppingBehavior || 'Not available'}</Typography>
              ) : (
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      filter: 'blur(5px)',
                      userSelect: 'none',
                      opacity: 0.6
                    }}
                  >
                    <Typography variant="body1">{persona.behaviorsAndHabits?.shoppingBehavior || 'Not available'}</Typography>
                  </Box>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      opacity: 0.5,
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                    onClick={handleTrialClick}
                  >
                    <VisibilityIcon fontSize="small" />
                    <Typography variant="caption" color="black" ml={1}>
                      View
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Technology & Media Usage */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" color="primary" gutterBottom>
                Technology & Media Usage
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Devices:
                </Typography>
                {isProUser ? (
                  hasTechnologyUsage && renderChips(persona.behaviorsAndHabits.technologyUsage)
                ) : (
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      sx={{
                        filter: 'blur(5px)',
                        userSelect: 'none',
                        opacity: 0.6
                      }}
                    >
                      {hasTechnologyUsage && renderChips(persona.behaviorsAndHabits.technologyUsage)}
                    </Box>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        opacity: 0.5,
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 1
                        }
                      }}
                      onClick={handleTrialClick}
                    >
                      <VisibilityIcon fontSize="small" />
                      <Typography variant="caption" color="black" ml={1}>
                        View
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Media Consumption:
                </Typography>
                {isProUser ? (
                  hasMediaConsumption && renderChips(persona.behaviorsAndHabits.mediaConsumption)
                ) : (
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      sx={{
                        filter: 'blur(5px)',
                        userSelect: 'none',
                        opacity: 0.6
                      }}
                    >
                      {hasMediaConsumption && renderChips(persona.behaviorsAndHabits.mediaConsumption)}
                    </Box>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        opacity: 0.5,
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 1
                        }
                      }}
                      onClick={handleTrialClick}
                    >
                      <VisibilityIcon fontSize="small" />
                      <Typography variant="caption" color="black" ml={1}>
                        View
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Digital Presence */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" color="primary" gutterBottom>
                Digital Presence
              </Typography>
              {['Social Media', 'Communities', 'Followed Influencers'].map((section) => (
                <Box key={section} sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {section}:
                  </Typography>
                  {isProUser ? (
                    renderChips(
                      section === 'Social Media'
                        ? hasDigitalAndSocial
                          ? persona.digitalAndSocial.socialMediaPlatforms
                          : []
                        : section === 'Communities'
                        ? hasOnlineCommunities
                          ? persona.digitalAndSocial.onlineCommunities
                          : []
                        : hasFollowedInfluencers
                        ? persona.digitalAndSocial.followedInfluencers
                        : []
                    )
                  ) : (
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        sx={{
                          filter: 'blur(5px)',
                          userSelect: 'none',
                          opacity: 0.6
                        }}
                      >
                        {renderChips(
                          section === 'Social Media'
                            ? hasDigitalAndSocial
                              ? persona.digitalAndSocial.socialMediaPlatforms
                              : []
                            : section === 'Communities'
                            ? hasOnlineCommunities
                              ? persona.digitalAndSocial.onlineCommunities
                              : []
                            : hasFollowedInfluencers
                            ? persona.digitalAndSocial.followedInfluencers
                            : []
                        )}
                      </Box>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          opacity: 0.5,
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 1
                          }
                        }}
                        onClick={handleTrialClick}
                      >
                        <VisibilityIcon fontSize="small" />
                        <Typography variant="caption" color="black" ml={1}>
                          View
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>

            {/* Communication Preferences */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" color="primary" gutterBottom>
                Communication Preferences
              </Typography>
              {['Channels', 'Content Preferences'].map((section) => (
                <Box key={section} sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    {section}:
                  </Typography>
                  {isProUser ? (
                    renderChips(
                      section === 'Channels'
                        ? hasPreferredCommunicationChannels
                          ? persona.preferredCommunicationChannels.channels
                          : []
                        : hasContentPreferences
                        ? persona.preferredCommunicationChannels.contentPreferences
                        : []
                    )
                  ) : (
                    <Box sx={{ position: 'relative' }}>
                      <Box
                        sx={{
                          filter: 'blur(5px)',
                          userSelect: 'none',
                          opacity: 0.6
                        }}
                      >
                        {renderChips(
                          section === 'Channels'
                            ? hasPreferredCommunicationChannels
                              ? persona.preferredCommunicationChannels.channels
                              : []
                            : hasContentPreferences
                            ? persona.preferredCommunicationChannels.contentPreferences
                            : []
                        )}
                      </Box>
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          opacity: 0.5,
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 1
                          }
                        }}
                        onClick={handleTrialClick}
                      >
                        <VisibilityIcon fontSize="small" />
                        <Typography variant="caption" color="black" ml={1}>
                          View
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>

            {/* Buying Behavior */}
            <Box>
              <Typography variant="h5" color="primary" gutterBottom>
                Buying Behavior
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Buying Triggers:
                </Typography>
                {isProUser ? (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {hasBuyingTriggersAndObjections &&
                      persona.buyingTriggersAndObjections.buyingTriggers.map((trigger, idx, arr) => (
                        <React.Fragment key={trigger + idx}>
                          <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mr: 1, mb: '-3px' }} />
                          {trigger}
                          {idx !== arr.length - 1 && <br />}
                        </React.Fragment>
                      ))}
                  </Typography>
                ) : (
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      sx={{
                        filter: 'blur(5px)',
                        userSelect: 'none',
                        opacity: 0.6
                      }}
                    >
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                        {hasBuyingTriggersAndObjections &&
                          persona.buyingTriggersAndObjections.buyingTriggers.map((trigger, idx, arr) => (
                            <React.Fragment key={trigger + idx}>
                              <ChevronRight sx={{ color: 'primary.main', fontSize: 20, mr: 1, mb: '-3px' }} />
                              {trigger}
                              {idx !== arr.length - 1 && <br />}
                            </React.Fragment>
                          ))}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        opacity: 0.5,
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 1
                        }
                      }}
                      onClick={handleTrialClick}
                    >
                      <VisibilityIcon fontSize="small" />
                      <Typography variant="caption" color="black" ml={1}>
                        View
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Common Objections:
                </Typography>
                {isProUser ? (
                  hasCommonObjections && renderList(persona.buyingTriggersAndObjections.commonObjections)
                ) : (
                  <Box sx={{ position: 'relative' }}>
                    <Box
                      sx={{
                        filter: 'blur(5px)',
                        userSelect: 'none',
                        opacity: 0.6
                      }}
                    >
                      {hasCommonObjections && renderList(persona.buyingTriggersAndObjections.commonObjections)}
                    </Box>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        opacity: 0.5,
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 1
                        }
                      }}
                      onClick={handleTrialClick}
                    >
                      <VisibilityIcon fontSize="small" />
                      <Typography variant="caption" color="black" ml={1}>
                        View
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PersonaB2CExtended;
